import { AiChatbotConversation, AiChatbotMessage, MessageRoleEnum } from '@va/dashboard/shared/ai-assistant';
import { useTranslate } from '@va/localization';
import { parseJson } from '@va/util/helpers';
import { ServerSentEventMessage } from 'fetch-event-stream';

import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { mapMessage, useGetConversation, useSendMessage, useStartNewConversation } from '../../../api';
import { AiChatbotView } from '../../../types';
import { useAiChatbotContext } from '../../aiChatbotContext';

export type ChatDetailsContextType = {
  isLoading: boolean;
  error?: Error;
  conversation: AiChatbotConversation | undefined;
  messages: Array<AiChatbotMessage> | undefined;
  mutateConversation: () => void;
  addMessage: (message: string) => void;
  liveMessage: string;
  isThinking: boolean;
  isStreamingMessage: boolean;
  stopStreamingMessage: () => void;
};

const ChatDetailsContext = createContext<ChatDetailsContextType>({} as ChatDetailsContextType);

export const ChatDetailsContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const translate = useTranslate();
  const { selectedModel, setSelectedView, selectedChat, setSelectedChat, mutateQuota } = useAiChatbotContext();
  const { data, isLoading, error, mutate } = useGetConversation({ conversationId: selectedChat?.id });
  const { execute: startNewConversation } = useStartNewConversation();
  const { execute: sendMessage } = useSendMessage();
  const [streamText, setStreamText] = useState<string>('');
  const [isThinking, setIsThinking] = useState(false);
  const [abortCtrl, setAbortCtrl] = useState(new AbortController());

  const mutateConversation = useCallback(
    ({ message, title, revalidate = false }: { message?: AiChatbotMessage; title?: string; revalidate?: boolean }) => {
      mutate(
        (currentData) => {
          if (!currentData) return;
          return {
            conversation: title ? { ...currentData.conversation, title } : currentData.conversation,
            messageHistory: message ? currentData.messageHistory.concat(message) : currentData.messageHistory,
          };
        },
        { revalidate },
      );
    },
    [mutate],
  );

  const onSendMessageEvent = useCallback(
    (event: ServerSentEventMessage) => {
      if (event.event === 'partialResponse' && event?.data) {
        const contentDelta = parseJson(event.data).contentDelta;
        setStreamText((p) => p + contentDelta);
      }
      if (event.event === 'fullResponse' && event?.data) {
        const { message, error } = parseJson(event.data);
        if (message) {
          mutateConversation({ message: mapMessage(message) });
          setStreamText('');
        }
        if (!message && error) {
          setStreamText(translate('aiChatbot.chat.errorMessage'));
          console.error(error);
        }
        setIsThinking(false);
        mutateQuota();
      }
    },
    [mutateConversation, mutateQuota, translate],
  );

  const onStartConversationEvent = useCallback(
    (event: ServerSentEventMessage) => {
      if (event.event === 'newConversation' && event?.data) {
        const id = parseJson(event.data).id;
        setSelectedChat({ id });
        setSelectedView(AiChatbotView.ChatDetails);
      }
      if (event.event === 'newTitle' && event?.data) {
        const title = parseJson(event.data).title;
        setSelectedChat((p) => ({ ...p, title }));
        mutateConversation({ title });
      }
      onSendMessageEvent(event);
    },
    [mutateConversation, onSendMessageEvent, setSelectedChat, setSelectedView],
  );

  const addMessage = useCallback(
    async (message: string) => {
      setStreamText('');
      setIsThinking(true);
      if (!selectedModel) return;

      const messageToAdd = {
        content: message,
        conversationId: selectedChat?.id ?? 'new',
        createdAt: new Date().toISOString(),
        id: crypto.randomUUID(),
        model: selectedModel,
        role: MessageRoleEnum.user,
        toolCalls: null,
        remoteId: null,
      };

      if (selectedChat) {
        mutateConversation({
          message: messageToAdd,
        });
      }

      let stream: AsyncGenerator<ServerSentEventMessage, void, unknown>;
      if (selectedChat?.id) {
        stream = await sendMessage(message, selectedModel, selectedChat?.id, abortCtrl);
      } else {
        stream = await startNewConversation(message, selectedModel, abortCtrl);
      }

      for await (const event of stream) {
        if (abortCtrl.signal.aborted) break;
        selectedChat?.id ? onSendMessageEvent(event) : onStartConversationEvent(event);
      }
      setAbortCtrl(new AbortController());
    },
    [
      selectedModel,
      selectedChat,
      mutateConversation,
      sendMessage,
      abortCtrl,
      startNewConversation,
      onSendMessageEvent,
      onStartConversationEvent,
    ],
  );

  const stopStreamingMessage = useCallback(() => {
    setAbortCtrl((p) => {
      p.abort();
      return new AbortController();
    });
    setStreamText('');
    setIsThinking(false);
  }, []);

  return (
    <ChatDetailsContext.Provider
      value={{
        conversation: data?.conversation,
        messages: data?.messageHistory,
        isLoading,
        error,
        mutateConversation: mutate,
        addMessage,
        liveMessage: streamText,
        isThinking,
        isStreamingMessage: !!streamText || isThinking,
        stopStreamingMessage: stopStreamingMessage,
      }}
    >
      {children}
    </ChatDetailsContext.Provider>
  );
};

export const useChatDetailsContext = () => useContext(ChatDetailsContext);
